import React, { useState, useEffect } from 'react';
import {
  Box, Container, Typography, Grid, Card, CardContent,
  Button, List, ListItem, ListItemIcon, ListItemText,
  Paper, ToggleButtonGroup, ToggleButton, CircularProgress
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { motion } from 'framer-motion';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import {
  getCurrentPricing,
  getCurrentAnnualPricing,
  getNttWeeklyPricing,
  getNttMonthlyPricing,
  getNttAnnualPricing,
  getNtWeeklyPricing,
  getNtMonthlyPricing,
  getNtAnnualPricing,
  isPromoActive
} from '../config/pricing.config';
import { PRICING_V2 } from '../config/pricing-v2.config';
import { cosmicAPI } from '../services/cosmicConciergeService';
import { analytics } from '../context/AnalyticsContext-v2';
import { useUser } from '../context/UserContext';
import { getForecastDiscount as getForecastDiscountPercent, isCalendarBonusActive, localYmd } from '../services/paymentService';
import { YA_BUNDLES, YA_OFFER_LAST_DAY, yaFullCents, yaPriceCents, yaEur } from '../config/yearAhead2027';

const SimplifiedTier = ({ tier, onCheckout, loading }) => (
  <Card
    sx={{
      height: '100%',
      position: 'relative',
      border: tier.popular ? '2px solid #c9a87c' : '1px solid rgba(201,168,124,0.15)',
      borderRadius: 3,
      bgcolor: tier.popular ? 'rgba(201,168,124,0.04)' : '#12121a',
      transform: tier.popular ? { xs: 'none', md: 'scale(1.05)' } : 'none',
    }}
  >
    {(tier.popular || tier.comingSoon) && (
      <Paper
        sx={{
          position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)',
          px: 3, py: 0.5, bgcolor: '#c9a87c', color: '#0a0a0f', borderRadius: 20,
        }}
      >
        <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>
          {tier.comingSoon ? 'COMING SOON' : 'MOST POPULAR'}
        </Typography>
      </Paper>
    )}
    <CardContent sx={{ p: { xs: 3, md: 4 }, pt: tier.popular || tier.comingSoon ? 5 : undefined }}>
      <Typography variant="h5" sx={{ fontWeight: 700, color: '#ede8e0', mb: 0.5 }}>
        {tier.name}
      </Typography>
      <Typography variant="body2" sx={{ color: '#8a8a9a', mb: 3 }}>
        {tier.description}
      </Typography>
      {tier.comingSoon ? (
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#c9a87c', mb: 3 }}>
          Coming soon
        </Typography>
      ) : (
        <>
          <Typography variant="h3" sx={{ fontWeight: 700, color: '#c9a87c', mb: 0.5 }}>
            EUR {tier.price.toFixed(2)}
          </Typography>
          <Typography variant="body2" sx={{ color: '#8a8a9a', mb: 3 }}>
            {tier.interval === 'month' ? '/month' : tier.interval === 'year' ? '/year' : 'one-time'}
            {tier.savings && <span style={{ color: '#4ade80', marginLeft: 8 }}>Save {tier.savings}</span>}
          </Typography>
        </>
      )}
      <List dense sx={{ mb: 3 }}>
        {tier.features.map((f, i) => (
          <ListItem key={i} sx={{ px: 0, py: 0.3 }}>
            <ListItemIcon sx={{ minWidth: 28 }}>
              <CheckCircleIcon sx={{ color: '#c9a87c', fontSize: 16 }} />
            </ListItemIcon>
            <ListItemText primary={f} primaryTypographyProps={{ variant: 'body2', sx: { color: '#ede8e0', fontSize: '0.8rem' } }} />
          </ListItem>
        ))}
      </List>
      <Button
        variant="contained"
        fullWidth
        size="large"
        onClick={() => onCheckout(tier.id)}
        disabled={tier.comingSoon || loading === tier.id}
        sx={{
          background: tier.popular
            ? 'linear-gradient(135deg, #c9a87c, #a88a5e)'
            : 'rgba(201,168,124,0.15)',
          color: tier.popular ? '#0a0a0f' : '#c9a87c',
          fontWeight: 700, py: 1.5,
          '&:hover': {
            background: tier.popular
              ? 'linear-gradient(135deg, #dbc4a0, #c9a87c)'
              : 'rgba(201,168,124,0.25)',
          },
        }}
      >
        {tier.comingSoon ? 'Coming soon' : loading === tier.id ? <CircularProgress size={20} /> : tier.buttonText || 'Get Started'}
      </Button>
    </CardContent>
  </Card>
);

const PricingPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [checkoutLoading, setCheckoutLoading] = useState(null);

  const userContext = useUser();
  const user = userContext?.user || null;
  const userId = user?.id || user?.userId || null;
  const userEmail = user?.email || null;

  // Subscriptions are not live yet (Claudia 2026-10-14): both plans show "Coming soon" and take no orders.
  // The Year Ahead forecast is sold in the Shop's "Year Ahead 2027" tab, through /year-ahead-2027.
  const simplifiedTiers = [
    {
      ...PRICING_V2.subscriptions.stars_made_simple_monthly,
      buttonText: 'Start Free Trial',
      popular: false,
      comingSoon: true,
    },
    {
      ...PRICING_V2.subscriptions.claudias_circle_monthly,
      buttonText: "Join Claudia's Circle",
      popular: false,
      comingSoon: true,
    },
  ];

  const handleSimplifiedCheckout = async (productId) => {
    setCheckoutLoading(productId);
    const product = simplifiedTiers.find((t) => t.id === productId);
    if (product) {
      analytics.beginCheckout(product.name, product.price);
    }
    try {
      const result = await cosmicAPI.createCheckout(productId, userId, userEmail);
      if (result.url || result.checkoutUrl) {
        window.location.href = result.url || result.checkoutUrl;
      }
    } catch (err) {
      console.error('Checkout failed:', err);
      setCheckoutLoading(null);
    }
  };

  // Get current pricing configuration
  const currentCalendarPricing = getCurrentPricing();
  const currentAnnualPricing = getCurrentAnnualPricing();
  const nttWeeklyPricing = getNttWeeklyPricing();
  const nttMonthlyPricing = getNttMonthlyPricing();
  const nttAnnualPricing = getNttAnnualPricing();
  const ntWeeklyPricing = getNtWeeklyPricing();
  const ntMonthlyPricing = getNtMonthlyPricing();
  const ntAnnualPricing = getNtAnnualPricing();
  const showPromo = isPromoActive();

  // Main tab: 'subscriptions' or 'shop'
  const urlSection = searchParams.get('section');
  const urlTab = searchParams.get('tab');
  // the Shop is the default while subscriptions are coming soon; ?section=subscriptions still opens them
  const [mainTab, setMainTab] = useState(() => (urlSection === 'subscriptions' ? 'subscriptions' : 'shop'));

  // Shop sub-tab
  const getInitialShopTab = () => {
    const s = urlTab || urlSection;
    if (s === 'calendar') return 'calendar';
    if (s === 'ntt-calendar') return 'ntt-calendar';
    if (s === 'nt-calendar') return 'nt-calendar';
    if (s === 'year-ahead') return 'year-ahead';
    return 'forecasts';
  };
  const [selectedSection, setSelectedSection] = useState(getInitialShopTab());

  // Update tabs if URL parameter changes
  useEffect(() => {
    const newSection = searchParams.get('section');
    const newTab = searchParams.get('tab');
    if (newSection === 'shop' || newSection === 'forecasts' || newSection === 'calendar' || newSection === 'ntt-calendar' || newSection === 'nt-calendar' || newSection === 'year-ahead') {
      setMainTab('shop');
      const t = newTab || newSection;
      if (t === 'calendar' || t === 'forecasts' || t === 'ntt-calendar' || t === 'nt-calendar' || t === 'year-ahead') {
        setSelectedSection(t);
      }
    } else if (newSection === 'subscriptions') {
      setMainTab('subscriptions');
    }
    // Scroll to top when arriving from another page
    window.scrollTo(0, 0);
  }, [searchParams, urlSection, urlTab, location.search]);

  // 2027 Deep Dive discount by the buyer's local date: until Oct 31 50% · Nov 30% · Dec 25% · 2027 full price.
  // The tiers live in paymentService.getForecastDiscount, and the order lambda charges the same.
  const getForecastDiscount = () => {
    const discountPercent = getForecastDiscountPercent();
    return { discountPercent, isActive: discountPercent > 0 };
  };

  // Calculate discounted price
  const calculateDiscountedPrice = (originalPrice) => {
    const { discountPercent, isActive } = getForecastDiscount();
    if (!isActive) return originalPrice;

    const discountedPrice = parseFloat(originalPrice) * (1 - discountPercent / 100);
    return discountedPrice.toFixed(2);
  };

  const handleCheckout = async (priceId, productType, requiresSignSelection, signCount, discountedPrice) => {
    // Store selection in sessionStorage
    sessionStorage.setItem('selectedProduct', productType);
    sessionStorage.setItem('priceId', priceId);
    // Store discounted price if applicable (forecast videos)
    if (discountedPrice) {
      sessionStorage.setItem('discountedPrice', discountedPrice);
    } else {
      sessionStorage.removeItem('discountedPrice');
    }

    // Navigate to sign selection or checkout
    if (requiresSignSelection) {
      // Store how many signs to select
      sessionStorage.setItem('signSelectionCount', signCount || '1');
      navigate('/select-signs');
    } else {
      navigate('/checkout');
    }
  };

  const discount = getForecastDiscount();

  const forecastPlans = [
    {
      name: 'Single Rising Sign',
      description: 'Perfect if you know your Rising Sign',
      originalPrice: '30.00',
      price: calculateDiscountedPrice('30.00'),
      priceId: 'price_single_rising',
      features: [
        'Almost 2-hour video deep dive',
        'House by House Transits & Degrees impacted',
        'Month by month timing guidance for major transits',
        'Monthly transit interactive map',
        'House interactive map',
        'Key Activation Windows',
        'House activations',
        'One-time purchase',
        'Lifetime access'
      ],
      buttonText: 'Choose Your Sign',
      popular: false,
      oneTime: true,
      requiresSignSelection: true,
      signCount: 1,
      hasDiscount: discount.isActive,
      discountPercent: discount.isActive ? discount.discountPercent : null
    },
    {
      name: 'Big Three Bundle',
      description: 'Choose any 3 signs - Perfect for your Rising, Sun & Moon',
      originalPrice: '50.00',
      price: calculateDiscountedPrice('50.00'),
      priceId: 'price_three_signs',
      features: [
        'Choose ANY 3 signs from all 12 zodiac signs',
        '3 almost-2-hour video deep dives',
        '3 monthly transit interactive maps',
        '3 house interactive maps',
        'House by House Transits & Degrees impacted (all 3 signs)',
        'Month by month timing guidance for major transits',
        'Key Activation Windows & House activations',
        'Email support',
        'One-time purchase',
        'Lifetime access'
      ],
      buttonText: 'Choose Your 3 Signs',
      popular: true,
      oneTime: true,
      requiresSignSelection: true,
      signCount: 3,
      hasDiscount: discount.isActive,
      discountPercent: discount.isActive ? discount.discountPercent : null
    },
    {
      name: 'All 12 Signs Bundle',
      description: 'Complete access to all zodiac sign forecasts',
      originalPrice: '100.00',
      price: calculateDiscountedPrice('100.00'),
      priceId: 'price_all_twelve_signs',
      features: [
        'All 12 almost-2-hour video deep dives',
        'All 12 monthly transit interactive maps',
        'All 12 house interactive maps',
        'House by House Transits & Degrees impacted (all signs)',
        'Month by month timing guidance for major transits',
        'Key Activation Windows & House activations (all signs)',
        'Priority email support',
        'One-time purchase',
        'Lifetime access'
      ],
      buttonText: 'Get All 12 Signs',
      popular: false,
      oneTime: true,
      hasDiscount: discount.isActive,
      discountPercent: discount.isActive ? discount.discountPercent : null
    }
  ];

  // What the current discount is called in the FAQ
  const saleLine = {
    50: 'the 50% early-bird price, through October 31',
    30: '30% off, through November 30',
    25: '25% off, through December 31'
  }[discount.discountPercent];

  // "Questions, answered" — FAQ for the 2027 deep-dive / pre-order shop
  const forecastFaqs = [
    {
      q: 'When do I pay — and when do I get everything?',
      a: `Order now${saleLine ? ` at ${saleLine},` : ''} and pay securely by card. Once your payment goes through, your deep-dive video and both interactive maps are emailed to you — usually within minutes, and always within 24 hours.`
    },
    {
      q: "What's included with each bundle?",
      a: 'For every sign you get the full deep-dive video — close to two hours — plus both interactive maps. Single Rising Sign covers one sign; the Big Three Bundle covers three; the All 12 Signs Bundle covers every rising sign. One-time purchase, lifetime access.'
    },
    {
      q: 'What are the two interactive maps?',
      a: "Two clickable charts that come with every sign. The transit map lays out every planet against the twelve months, colour-coded by the aspect it makes to you. The house map shows which area of your life each planet lights up. Click any cell and it opens into a full written interpretation — there's more detail in the maps than the video itself can cover."
    },
    {
      q: 'How is this different from the free 2027 Cosmic Overview?',
      a: 'The free 2027 Cosmic Overview on YouTube is 25–30 minutes on the year’s major themes. The extended deep dive is close to two hours — house-by-house transits, specific timing, detailed manifestations and month-by-month guidance for major transits — plus the two interactive maps.'
    },
    {
      q: 'What is a “rising sign,” and do I need my exact birth time?',
      a: 'Your rising sign (or ascendant) is the sign climbing over the eastern horizon at the moment you were born — the lens the whole year is read through, which is why the forecast is built around it rather than just your Sun sign. You’ll want your exact birth time for it. Not sure of yours? You can find it free on ivyastrology.com with your birth date, time and place.'
    },
    {
      q: 'Why the “Big Three”?',
      a: 'Your Sun, Moon and Rising are the three placements that shape the most — your core, your inner world, and the way you meet life. Reading all three together gives the fullest picture of your 2027, which is why it’s the bundle most people choose.'
    },
    {
      q: 'Is this a one-time payment or a subscription?',
      a: 'A one-time purchase with lifetime access to your videos and maps — no recurring charges or hidden fees. (The optional Ivy Astrology calendars are separate subscriptions.)'
    },
    // calendar-code bonus: deep-dive orders placed in September or October only
    ...(isCalendarBonusActive() ? [{
      q: 'Do I also get a discount on the calendars?',
      a: "Yes. Order the 2027 deep dive by October 31 and your delivery includes a calendar code: 50% off the monthly calendar every month for 12 months, or 50% off the yearly calendar for 2027 (your first year). It's the tool that tells you the exact day each transit touches your chart. And the weekly calendar view is free for everyone, always."
    }] : [])
  ];

  // Cosmic Personal Events Calendar (Transit-to-Transit)
  const calendarPlans = [
    {
      name: 'Monthly Cosmic Personal Events',
      description: currentCalendarPricing.description,
      price: currentCalendarPricing.price,
      priceId: 'price_monthly_calendar_2026',
      features: currentCalendarPricing.features,
      buttonText: 'Get 30-Day Access',
      popular: true,
      oneTime: false,
      comingSoon: false,
      isPromo: showPromo
    },
    {
      name: 'Annual Cosmic Personal Events',
      description: currentAnnualPricing.description,
      price: currentAnnualPricing.price,
      priceId: 'price_annual_calendar_2026',
      features: currentAnnualPricing.features,
      buttonText: 'Get Full Year Access',
      popular: false,
      oneTime: false,
      comingSoon: false,
      isPromo: showPromo
    }
  ];

  // Personal Cosmic Activation (Natal-to-Transit-to-Transit)
  // Weekly view is free for everyone in the calendar app, so only the paid Monthly & Annual plans are shown here.
  const nttCalendarPlans = [
    {
      name: 'Monthly Personal Cosmic Activation',
      description: nttMonthlyPricing.description,
      price: nttMonthlyPricing.price,
      priceId: 'price_ntt_monthly_calendar_2026',
      features: nttMonthlyPricing.features,
      buttonText: 'Get 30-Day Access',
      popular: true,
      oneTime: false,
      comingSoon: false,
      isPromo: showPromo
    },
    {
      name: 'Annual Personal Cosmic Activation',
      description: nttAnnualPricing.description,
      price: nttAnnualPricing.price,
      priceId: 'price_ntt_annual_calendar_2026',
      features: nttAnnualPricing.features,
      buttonText: 'Get Full Year Access',
      popular: false,
      oneTime: false,
      comingSoon: false,
      isPromo: showPromo
    }
  ];

  // Personal Transit Calendar (Natal-to-Transit)
  // Weekly view is free for everyone in the calendar app, so only the paid Monthly & Annual plans are shown here.
  const ntCalendarPlans = [
    {
      name: 'Monthly Personal Transit',
      description: ntMonthlyPricing.description,
      price: ntMonthlyPricing.price,
      priceId: 'price_nt_monthly_calendar_2026',
      features: ntMonthlyPricing.features,
      buttonText: 'Get 30-Day Access',
      popular: true,
      oneTime: false,
      comingSoon: false,
      isPromo: showPromo,
      hasDiscount: showPromo,
      originalPrice: showPromo ? '9.99' : null,
      discountPercent: showPromo ? 50 : null
    },
    {
      name: 'Annual Personal Transit',
      description: ntAnnualPricing.description,
      price: ntAnnualPricing.price,
      priceId: 'price_nt_annual_calendar_2026',
      features: ntAnnualPricing.features,
      buttonText: 'Get Full Year Access',
      popular: false,
      oneTime: false,
      comingSoon: false,
      isPromo: showPromo,
      hasDiscount: showPromo,
      originalPrice: showPromo ? '99.99' : null,
      discountPercent: showPromo ? 50 : null
    }
  ];

  const currentPlans = selectedSection === 'forecasts' ? forecastPlans : selectedSection === 'ntt-calendar' ? nttCalendarPlans : selectedSection === 'nt-calendar' ? ntCalendarPlans : calendarPlans;

  const mainTabStyle = (value) => ({
    px: { xs: 3, sm: 6 }, py: { xs: 1.5, sm: 2 },
    fontSize: { xs: '1rem', sm: '1.15rem' }, fontWeight: 700,
    borderRadius: '12px 12px 0 0',
    '&.Mui-selected': {
      bgcolor: mainTab === 'subscriptions' ? '#0a0a0f' : 'grey.50',
      color: mainTab === 'subscriptions' ? '#c9a87c' : 'primary.main',
      '&:hover': { bgcolor: mainTab === 'subscriptions' ? '#0a0a0f' : 'grey.50' },
    },
  });

  const shopSubTabStyle = {
    px: { xs: 2, sm: 4 }, py: { xs: 1.5, sm: 2 },
    fontSize: { xs: '0.875rem', sm: '1rem' }, fontWeight: 600,
    color: '#8a8a9a',
    '&.Mui-selected': { bgcolor: 'primary.main', color: '#0a0a0f', '&:hover': { bgcolor: 'primary.dark' } },
  };

  return (
    <Box sx={{ minHeight: '100vh' }}>
      {/* ============ MAIN TAB BAR ============ */}
      <Box sx={{ bgcolor: '#151520', pt: { xs: 3, md: 5 } }}>
        <Container maxWidth="lg">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <Typography
              variant="h2" align="center" gutterBottom
              sx={{
                fontWeight: 700, fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                background: 'linear-gradient(135deg, #ede8e0, #c9a87c)',
                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
              }}
            >
              Choose Your Cosmic Journey
            </Typography>
            <Typography variant="h6" align="center" sx={{ color: '#8a8a9a', mb: 4 }}>
              From daily guidance to one-off cosmic tools
            </Typography>
          </motion.div>

          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <ToggleButtonGroup
              value={mainTab} exclusive
              onChange={(e, v) => v && setMainTab(v)}
              sx={{ bgcolor: 'rgba(201,168,124,0.08)', borderRadius: '12px 12px 0 0', border: '1px solid rgba(201,168,124,0.15)', borderBottom: 'none' }}
            >
              <ToggleButton value="subscriptions" sx={mainTabStyle('subscriptions')}>
                Subscriptions
              </ToggleButton>
              <ToggleButton value="shop" sx={mainTabStyle('shop')}>
                Shop
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Container>
      </Box>

      {/* ============ SUBSCRIPTIONS TAB ============ */}
      {mainTab === 'subscriptions' && (
        <Box sx={{ py: { xs: 6, md: 8 }, bgcolor: '#0a0a0f' }}>
          <Container maxWidth="lg">
            <Typography variant="h6" align="center" sx={{ color: '#8a8a9a', mb: 1 }}>
              Monthly & annual plans — from daily guidance to the full VIP experience
            </Typography>
            <Typography align="center" sx={{ color: '#c9a87c', mb: 6 }}>
              Subscriptions are coming soon. Until then, everything you can order is in the{' '}
              <Box component="span" onClick={() => setMainTab('shop')} sx={{ textDecoration: 'underline', cursor: 'pointer', fontWeight: 600 }}>Shop</Box>.
            </Typography>
            <Grid container spacing={3} justifyContent="center" alignItems="stretch">
              {simplifiedTiers.map((tier, idx) => (
                <Grid item xs={12} md={4} key={tier.id}>
                  <motion.div
                    initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: idx * 0.1 }}
                    style={{ height: '100%' }}
                  >
                    <SimplifiedTier tier={tier} onCheckout={handleSimplifiedCheckout} loading={checkoutLoading} />
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>
      )}

      {/* ============ SHOP TAB ============ */}
      {mainTab === 'shop' && (
      <Box sx={{ py: 8, bgcolor: '#0a0a0f' }}>
      <Container maxWidth="lg">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <Typography variant="h6" align="center" color="text.secondary" paragraph sx={{ fontSize: { xs: '1rem', sm: '1.125rem' }, mb: 4 }}>
            One-off purchases — forecast videos, the Year Ahead 2027 maps & calendar access
          </Typography>

          {/* Shop Sub-Tabs */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 6 }}>
            <ToggleButtonGroup
              value={selectedSection} exclusive
              onChange={(e, value) => value && setSelectedSection(value)}
              sx={{ bgcolor: '#1a1a2e', boxShadow: 2, borderRadius: 2, flexDirection: { xs: 'column', sm: 'row' }, width: { xs: '100%', sm: 'auto' }, border: '1px solid rgba(201,168,124,0.12)' }}
            >
              <ToggleButton value="forecasts" sx={shopSubTabStyle}>
                2027 Extended Forecast Videos
              </ToggleButton>
              <ToggleButton value="year-ahead" sx={shopSubTabStyle}>
                Year Ahead 2027
              </ToggleButton>
              <ToggleButton value="calendar" sx={shopSubTabStyle}>
                Cosmic Personal Events Calendar
              </ToggleButton>
              <ToggleButton value="ntt-calendar" sx={shopSubTabStyle}>
                Personal Cosmic Activation
              </ToggleButton>
              <ToggleButton value="nt-calendar" sx={shopSubTabStyle}>
                Personal Transit Calendar
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </motion.div>

        {/* Section Title */}
        <motion.div
          key={selectedSection}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <Typography
            variant="h4"
            align="center"
            gutterBottom
            sx={{
              fontWeight: 600,
              mb: 4,
              fontSize: { xs: '1.5rem', sm: '2rem' }
            }}
          >
            {selectedSection === 'forecasts'
              ? 'Choose Your 2027 Extended Forecast Video'
              : selectedSection === 'year-ahead'
              ? 'Your Year Ahead 2027 Road Map'
              : selectedSection === 'ntt-calendar'
              ? 'Personal Cosmic Activation'
              : selectedSection === 'nt-calendar'
              ? 'Personal Transit Calendar'
              : 'Cosmic Personal Events Calendar'
            }
          </Typography>
          <Typography
            variant="body1"
            align="center"
            color="text.secondary"
            paragraph
            sx={{ mb: 5, fontSize: { xs: '0.875rem', sm: '1rem' } }}
          >
            {selectedSection === 'forecasts'
              ? 'Unlock detailed predictions for your Rising Sign'
              : selectedSection === 'year-ahead'
              ? 'Your 2027 house map and transit map for your rising sign, emailed straight after payment. One sign, or save with a bundle.'
              : selectedSection === 'ntt-calendar'
              ? 'Track when current planets aspect your natal chart with personalized calendar views'
              : 'Track your daily transits with personalized calendar views'
            }
          </Typography>
        </motion.div>

        {/* Weekly view is free — only Monthly & Annual are paid upgrades */}
        {selectedSection !== 'forecasts' && selectedSection !== 'year-ahead' && (
          <Typography align="center" sx={{ mb: 5, mt: -3, color: '#c9a87c', fontSize: { xs: '0.85rem', sm: '0.95rem' } }}>
            ✨ The <strong>weekly view is free for everyone</strong> — the Monthly and Annual plans below unlock the full year ahead.
          </Typography>
        )}

        {/* Year Ahead 2027: the bundles of /year-ahead-2027 (src/config/yearAhead2027.js). Every card opens that
            page with the bundle preselected, so the sign form, the payment and the map email are the same. */}
        {selectedSection === 'year-ahead' && (
          <>
            {localYmd() <= YA_OFFER_LAST_DAY && (
              <Typography align="center" sx={{ mb: 5, mt: -3, color: '#c9a87c', fontSize: { xs: '0.85rem', sm: '0.95rem' } }}>
                ✨ Free until 31 October with your{' '}
                <Box component="span" onClick={() => setSelectedSection('forecasts')} sx={{ textDecoration: 'underline', cursor: 'pointer', fontWeight: 600 }}>2027 Extended Forecast video</Box>.
              </Typography>
            )}
            <Grid container spacing={3} justifyContent="center">
              {YA_BUNDLES.map((b, index) => (
                <Grid item xs={12} sm={6} md={3} key={b.id}>
                  <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: index * 0.1 }} style={{ height: '100%' }}>
                    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', boxShadow: 2, border: b.discount ? '1px solid rgba(201,168,124,0.35)' : 'none' }}>
                      <CardContent sx={{ p: 3, flex: 1, display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>{b.label}</Typography>
                        <Typography color="text.secondary" sx={{ fontSize: '0.9rem', mb: 2 }}>
                          {b.count === 12
                            ? 'Every sign: the 2027 house and transit maps for all twelve'
                            : b.count === 1
                              ? 'Your rising sign: its 2027 house and transit maps'
                              : `Any ${b.count} signs: for you, your family and friends`}
                        </Typography>
                        <Box sx={{ mb: 3 }}>
                          {b.discount > 0 && (
                            <Typography sx={{ color: 'text.secondary', textDecoration: 'line-through', fontSize: '1.1rem' }}>{yaEur(yaFullCents(b))}</Typography>
                          )}
                          <Typography variant="h3" sx={{ fontWeight: 700, fontSize: '2.1rem' }}>{yaEur(yaPriceCents(b))}</Typography>
                          {b.discount > 0 && <Typography sx={{ color: '#c41e3a', fontWeight: 700 }}>{b.discount}% OFF</Typography>}
                        </Box>
                        <Button
                          variant="contained"
                          fullWidth
                          size="large"
                          onClick={() => navigate(`/year-ahead-2027?bundle=${b.id}`)}
                          sx={{ mt: 'auto', py: 1.5, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', '&:hover': { background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)' } }}
                        >
                          {b.count === 1 ? 'Choose your sign' : b.count === 12 ? 'Get all 12' : `Choose ${b.count} signs`}
                        </Button>
                      </CardContent>
                    </Card>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </>
        )}

        {/* Pricing Cards */}
        {selectedSection !== 'year-ahead' && (
        <Grid container spacing={4} justifyContent="center">
          {currentPlans.map((plan, index) => (
            <Grid
              item
              xs={12}
              sm={selectedSection === 'forecasts' ? 6 : 12}
              md={selectedSection === 'forecasts' ? 4 : 6}
              key={index}
            >
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                style={{ height: '100%' }}
              >
                <Card
                  sx={{
                    height: '100%',
                    position: 'relative',
                    border: plan.popular ? '2px solid' : 'none',
                    borderColor: 'primary.main',
                    transform: plan.popular ? { xs: 'none', md: 'scale(1.05)' } : 'none',
                    boxShadow: plan.popular ? 8 : 2,
                    mt: plan.popular ? { xs: 0, md: 4 } : 0,
                  }}
                >
                  {(plan.popular || plan.isPromo) && (
                    <Paper
                      sx={{
                        position: 'absolute',
                        top: 8,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        px: 3,
                        py: 0.5,
                        bgcolor: plan.isPromo ? '#f59e0b' : 'primary.main',
                        color: 'white',
                        borderRadius: 20,
                      }}
                    >
                      <Typography variant="caption" sx={{ fontWeight: 600, fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                        {plan.isPromo
                          ? (plan.name === 'Annual Cosmic Personal Events' ? currentAnnualPricing.badgeText :
                             plan.name === 'Annual Personal Cosmic Activation' ? nttAnnualPricing.badgeText :
                             plan.name === 'Monthly Personal Cosmic Activation' ? nttMonthlyPricing.badgeText :
                             plan.name === 'Weekly Personal Cosmic Activation' ? nttWeeklyPricing.badgeText :
                             plan.name === 'Annual Personal Transit' ? ntAnnualPricing.badgeText :
                             plan.name === 'Monthly Personal Transit' ? ntMonthlyPricing.badgeText :
                             plan.name === 'Weekly Personal Transit' ? ntWeeklyPricing.badgeText :
                             currentCalendarPricing.badgeText)
                          : (plan.popular ? 'MOST POPULAR' :
                             (plan.name === 'Annual Cosmic Personal Events' || plan.name === 'Annual Personal Cosmic Activation' || plan.name === 'Annual Personal Transit') ? 'BEST VALUE' :
                             (plan.name === 'Weekly Personal Cosmic Activation' || plan.name === 'Weekly Personal Transit') ? 'WEEKLY ACCESS' : 'MONTHLY ACCESS')}
                      </Typography>
                    </Paper>
                  )}

                  <CardContent sx={{ p: { xs: 3, sm: 4 }, pt: (plan.popular || plan.isPromo) ? { xs: 6, sm: 7 } : { xs: 3, sm: 4 } }}>
                    <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, fontSize: { xs: '1.5rem', sm: '1.75rem' } }}>
                      {plan.name}
                    </Typography>
                    <Typography color="text.secondary" paragraph sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                      {plan.description}
                    </Typography>

                    {/* Price */}
                    <Box sx={{ my: 3 }}>
                      {plan.comingSoon ? (
                        <Typography variant="h5" sx={{ fontWeight: 600, color: 'primary.main', fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                          Pricing To Be Announced
                        </Typography>
                      ) : plan.price ? (
                        <>
                          {/* Show original price with strikethrough if discount is active */}
                          {plan.hasDiscount && plan.originalPrice && (
                            <Box sx={{ mb: 1 }}>
                              <Typography
                                variant="h5"
                                sx={{
                                  fontWeight: 500,
                                  color: 'text.secondary',
                                  textDecoration: 'line-through',
                                  fontSize: { xs: '1.25rem', sm: '1.5rem' }
                                }}
                              >
                                {selectedSection === 'forecasts' ? '€' : '$'}{plan.originalPrice}
                              </Typography>
                              {plan.discountPercent !== null && plan.discountPercent !== undefined && (
                                <Typography
                                  variant="body1"
                                  sx={{
                                    color: '#c41e3a',
                                    fontWeight: 700,
                                    fontSize: { xs: '0.875rem', sm: '1rem' }
                                  }}
                                >
                                  {plan.discountPercent}% OFF
                                </Typography>
                              )}
                            </Box>
                          )}

                          <Typography variant="h2" sx={{ fontWeight: 700, display: 'inline', fontSize: { xs: '2rem', sm: '2.5rem' } }}>
                            {plan.price === 'FREE' ? 'FREE' : `${selectedSection === 'forecasts' ? '€' : '$'}${plan.price}`}
                          </Typography>
                          {plan.price !== 'FREE' && (
                            <Typography variant="h6" sx={{ display: 'inline', ml: 1, color: 'text.secondary', fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                              {plan.oneTime ? 'one-time' :
                               ((plan.name === 'Annual Cosmic Personal Events' || plan.name === 'Annual Personal Cosmic Activation' || plan.name === 'Annual Personal Transit') ? '/year' :
                                (plan.name === 'Weekly Personal Cosmic Activation' || plan.name === 'Weekly Personal Transit') ? '/week' : '/mo')}
                            </Typography>
                          )}
                          {plan.isPromo && (
                            <Typography variant="body2" sx={{ mt: 1, color: '#f59e0b', fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                              {plan.name === 'Annual Cosmic Personal Events' ? currentAnnualPricing.subText :
                               plan.name === 'Annual Personal Cosmic Activation' ? nttAnnualPricing.subText :
                               plan.name === 'Monthly Personal Cosmic Activation' ? nttMonthlyPricing.subText :
                               plan.name === 'Weekly Personal Cosmic Activation' ? nttWeeklyPricing.subText :
                               plan.name === 'Annual Personal Transit' ? ntAnnualPricing.subText :
                               plan.name === 'Monthly Personal Transit' ? ntMonthlyPricing.subText :
                               plan.name === 'Weekly Personal Transit' ? ntWeeklyPricing.subText :
                               currentCalendarPricing.subText}
                            </Typography>
                          )}
                        </>
                      ) : (
                        <Typography variant="h6" color="text.secondary">
                          Not available as subscription
                        </Typography>
                      )}
                    </Box>

                    {/* Features */}
                    <List dense sx={{ mb: 3 }}>
                      {plan.features.map((feature, idx) => (
                        <ListItem key={idx} sx={{ px: 0 }}>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <CheckCircleIcon color="success" fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary={feature}
                            primaryTypographyProps={{ variant: 'body2', fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                          />
                        </ListItem>
                      ))}
                    </List>

                    {/* CTA Button */}
                    <Button
                      variant={plan.comingSoon ? 'outlined' : 'contained'}
                      fullWidth
                      size="large"
                      onClick={() => handleCheckout(plan.priceId, plan.name, plan.requiresSignSelection, plan.signCount, plan.hasDiscount ? plan.price : null)}
                      disabled={plan.comingSoon || (!plan.price && plan.oneTime)}
                      sx={{
                        py: { xs: 1.5, sm: 2 },
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                        background: plan.comingSoon ? 'transparent' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        borderColor: plan.comingSoon ? 'grey.400' : undefined,
                        color: plan.comingSoon ? 'text.disabled' : undefined,
                        cursor: plan.comingSoon ? 'not-allowed' : 'pointer',
                        '&:hover': plan.comingSoon ? {
                          background: 'transparent',
                          borderColor: 'grey.400'
                        } : {
                          background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                        },
                        '&.Mui-disabled': {
                          borderColor: 'grey.400',
                          color: 'text.disabled',
                          opacity: 0.7
                        }
                      }}
                    >
                      {plan.buttonText}
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>
        )}

        {/* Money-back Guarantee - Only show for forecasts */}
        {selectedSection === 'forecasts' && (
          <Paper sx={{ mt: 6, p: { xs: 3, sm: 4 }, textAlign: 'center', bgcolor: 'rgba(201,168,124,0.08)', border: '1px solid rgba(201,168,124,0.15)' }}>
            <Typography variant="h5" gutterBottom sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
              ✨ 30-Day Money-Back Guarantee
            </Typography>
            <Typography color="text.secondary" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
              Not satisfied? Get a full refund within 30 days. No questions asked.
            </Typography>
          </Paper>
        )}

        {/* "Questions, answered" — only on the forecast deep-dive shop */}
        {selectedSection === 'forecasts' && (
          <Box sx={{ mt: 8 }}>
            <Typography variant="h3" align="center" gutterBottom sx={{ fontSize: { xs: '1.75rem', sm: '2.5rem' } }}>
              Questions, answered
            </Typography>

            <Grid container spacing={3} sx={{ mt: 3 }}>
              {forecastFaqs.map((faq, i) => (
                <Grid item xs={12} md={6} key={i}>
                  <Paper sx={{ p: { xs: 2, sm: 3 }, height: '100%' }}>
                    <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                      {faq.q}
                    </Typography>
                    <Typography color="text.secondary" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                      {faq.a}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
      </Container>
      </Box>
      )}
    </Box>
  );
};

export default PricingPage;
